import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, Clock, AlertTriangle, Guitar, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDatePt } from '@/components/Badges';

const ERROR_MESSAGES = {
  token_invalid: {
    title: 'Código de votação inválido',
    text: 'Verifique sua comanda ou procure a equipe do Pub.',
    icon: XCircle,
  },
  already_used: {
    title: 'Esta comanda já foi utilizada para votar',
    text: 'Obrigado por participar!',
    icon: CheckCircle2,
  },
  cancelled: {
    title: 'Esta comanda foi cancelada',
    text: 'Procure a equipe do Pub.',
    icon: XCircle,
  },
  not_open_yet: {
    title: 'Votação ainda não aberta',
    text: 'Aguarde o anúncio da equipe do Pub Garage 66.',
    icon: Clock,
  },
  voting_closed: {
    title: 'Votação encerrada',
    text: 'Obrigado por participar! Acompanhe os resultados na tela do pub.',
    icon: Clock,
  },
  stage_cancelled: {
    title: 'Etapa cancelada',
    text: 'Esta etapa foi cancelada. Procure a equipe do Pub.',
    icon: AlertTriangle,
  },
  stage_not_found: {
    title: 'Etapa não encontrada',
    text: 'Procure a equipe do Pub.',
    icon: XCircle,
  },
  bands_missing: {
    title: 'Bandas não configuradas',
    text: 'Aguarde a organização completar o cadastro.',
    icon: AlertTriangle,
  },
};

export default function VotePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState(null);
  const [selected, setSelected] = useState(null); // 'a' or 'b'
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [nightPassword, setNightPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    api.get(`/vote/validate/${token}`)
      .then((r) => setValidation(r.data))
      .catch(() => setValidation({ valid: false, reason: 'token_invalid' }))
      .finally(() => setLoading(false));
  }, [token]);

  const handleVote = async () => {
    if (!selected) return;
    setSubmitting(true);
    setErrorMsg(null);
    const bandsByKey = { a: validation.band_a, b: validation.band_b, c: validation.band_c };
    const band_id = bandsByKey[selected]?.id;
    try {
      await api.post(`/vote/${token}`, {
        band_id,
        night_password: validation.stage.require_night_password ? nightPassword : null,
      });
      setSuccess(true);
      setConfirmOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erro ao registrar voto. Tente novamente.';
      setErrorMsg(detail);
      setConfirmOpen(false);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <VoteShell>
        <div className="text-center text-muted-foreground" data-testid="vote-loading-state">
          <Loader2 className="w-8 h-8 mx-auto animate-spin" />
          <div className="mt-3">Carregando disputa…</div>
        </div>
      </VoteShell>
    );
  }

  if (success) {
    return (
      <VoteShell>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="text-center"
          data-testid="vote-success-state"
        >
          <div className="mx-auto w-20 h-20 rounded-full bg-[color:hsla(42,92%,52%,0.16)] flex items-center justify-center border border-[color:hsla(42,92%,52%,0.5)]">
            <CheckCircle2 className="w-10 h-10 text-[var(--g66-amber)]" />
          </div>
          <div className="font-display text-3xl text-white mt-4">VOTO REGISTRADO!</div>
          <p className="text-muted-foreground mt-2">
            Obrigado por participar do Campeonato de Bandas do Pub Garage 66.
          </p>
          <p className="text-xs text-muted-foreground mt-4">Você votou em:</p>
          <div className="font-display text-2xl text-[var(--g66-amber)] mt-1">
            {selected === 'a' ? validation.band_a?.name : selected === 'b' ? validation.band_b?.name : validation.band_c?.name}
          </div>
        </motion.div>
      </VoteShell>
    );
  }

  if (!validation?.valid) {
    const err = ERROR_MESSAGES[validation?.reason] || ERROR_MESSAGES.token_invalid;
    const Icon = err.icon;
    return (
      <VoteShell>
        <div className="text-center" data-testid={`vote-error-${validation?.reason || 'token_invalid'}`}>
          <div className="mx-auto w-20 h-20 rounded-full bg-[color:hsla(0,78%,52%,0.14)] flex items-center justify-center border border-[color:hsla(0,78%,52%,0.35)]">
            <Icon className="w-10 h-10 text-[var(--g66-red-2)]" />
          </div>
          <div className="font-display text-2xl text-white mt-4">{err.title}</div>
          <p className="text-muted-foreground mt-2">{err.text}</p>
          <Link to="/" className="inline-block mt-6"><Button variant="secondary">Voltar ao início</Button></Link>
        </div>
      </VoteShell>
    );
  }

  const stage = validation.stage;
  const bandA = validation.band_a;
  const bandB = validation.band_b;
  const bandC = validation.band_c;
  const selectedBand = selected === 'a' ? bandA : selected === 'b' ? bandB : selected === 'c' ? bandC : null;

  return (
    <VoteShell>
      <div data-testid="vote-ready-state">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-[var(--g66-amber)]">{formatDatePt(stage.date)}</div>
          <div className="font-display text-2xl text-white mt-1">{stage.title}</div>
          <div className="text-xs text-muted-foreground mt-1">Comanda #{validation.command?.visible_number}</div>
        </div>

        <div className="mt-6 text-center text-sm text-muted-foreground bg-[var(--g66-surface)] border border-border rounded-lg p-3">
          <Info className="w-4 h-4 inline mr-1 -mt-0.5" /> Você só pode votar uma vez. Confira sua escolha antes de confirmar.
        </div>

        {stage.require_night_password && (
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Senha da noite</label>
            <Input
              data-testid="vote-night-password-input"
              placeholder="Digite a senha anunciada no Pub"
              value={nightPassword}
              onChange={(e) => setNightPassword(e.target.value)}
              className="mt-1 bg-card border-border"
            />
          </div>
        )}

        <div className="mt-6 space-y-4">
          <VoteButton band={bandA} onClick={() => { setSelected('a'); setConfirmOpen(true); }} testId="vote-band-a-button" />
          <VoteButton band={bandB} onClick={() => { setSelected('b'); setConfirmOpen(true); }} testId="vote-band-b-button" />
          {bandC && (
            <VoteButton band={bandC} onClick={() => { setSelected('c'); setConfirmOpen(true); }} testId="vote-band-c-button" />
          )}
        </div>

        {errorMsg && (
          <div className="mt-4 rounded-lg border border-[color:hsla(0,78%,52%,0.4)] bg-[color:hsla(0,78%,52%,0.08)] p-3 text-sm text-[var(--g66-red-2)]" data-testid="vote-error-inline">
            {errorMsg}
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent className="bg-card border border-border" data-testid="confirm-modal">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl">Confirmar voto</AlertDialogTitle>
              <AlertDialogDescription>
                Você está votando em <span className="text-[var(--g66-amber)] font-semibold">{selectedBand?.name}</span>. Após confirmar, não é possível alterar.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-modal-cancel-button" disabled={submitting}>Voltar</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-modal-confirm-button"
                onClick={handleVote}
                disabled={submitting}
                className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enviando…</> : 'Confirmar voto'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </VoteShell>
  );
}

function VoteButton({ band, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="vote-btn w-full min-h-[104px] rounded-2xl bg-[var(--g66-surface)] border border-border hover:border-[color:hsla(42,92%,52%,0.6)] px-4 py-4 text-left flex items-center gap-4"
    >
      <div className="w-16 h-16 rounded-xl bg-[#0b0c0f] flex items-center justify-center border border-border overflow-hidden shrink-0">
        {band?.logo_url ? <img src={band.logo_url} alt={band.name} className="w-full h-full object-cover" /> : <Guitar className="w-7 h-7 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-2xl text-white leading-none">{band?.name}</div>
        <div className="text-xs text-muted-foreground mt-1">{band?.city}{band?.state ? `/${band.state}` : ''}</div>
        <div className="text-xs text-[var(--g66-amber)] mt-2 uppercase tracking-widest">Votar nesta banda →</div>
      </div>
    </button>
  );
}

function VoteShell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-4 py-4 border-b border-border">
        <div className="mx-auto max-w-md flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[var(--g66-amber)] flex items-center justify-center text-black">
            <Guitar className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg text-white">GARAGE 66</div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Battle Vote</div>
          </div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md pb-[max(1.25rem,env(safe-area-inset-bottom))]">{children}</div>
      </main>
    </div>
  );
}
